import { z } from 'zod'
import { idParam } from './id-validators'
import { partialZonderDefaults } from './zod-patch'

/**
 * FO-018 (batch-14) - tankpas: beheerbare leverancier-lijst (default "MKB
 * Brandstof") + pincode (default 7710, zichtbaar). Gedeeld request-schema zodat
 * de API en de forms dezelfde validatie gebruiken (CLAUDE.md). Het tankpas-record
 * wordt ook in de IC-stroom gebruikt - beide schermen blijven consistent.
 */

// batch-70 wagenpark-week (FO-529 §A2) - ONGEBRUIKT: de pas is actief bij de leverancier
// maar heeft geen houder (het voertuig heeft geen bestuurder meer). Wordt gezet bij een
// intrekking zonder nieuwe bestuurder en weer ACTIEF zodra het voertuig een bestuurder krijgt.
export const TANKPAS_STATUSSEN = ['ACTIEF', 'ONGEBRUIKT', 'GEBLOKKEERD', 'VERLOREN', 'GERETOURNEERD'] as const
export type TankpasStatus = (typeof TANKPAS_STATUSSEN)[number]

export const TANKPAS_STATUS_LABELS: Record<TankpasStatus, string> = {
  ACTIEF: 'Actief',
  ONGEBRUIKT: 'Ongebruikt (geen houder)',
  GEBLOKKEERD: 'Geblokkeerd',
  VERLOREN: 'Verloren',
  GERETOURNEERD: 'Geretourneerd',
}

/** batch-70 wagenpark-week - bij de leverancier nog bruikbaar: met deze pas kan getankt worden. */
export const TANKPAS_IN_OMLOOP: readonly TankpasStatus[] = ['ACTIEF', 'ONGEBRUIKT']

/** Een tankpas-leverancier uit de beheerbare lijst. */
export interface TankpasLeverancierDTO {
  id: string
  naam: string
  actief: boolean
}

/** Tankpas-rij zoals GET /ic/tankpassen (Materieel + IC) toont. */
export interface FuelCardRowDTO {
  id: string
  passnummer: string
  /** Weergavenaam van de leverancier (uit de beheerlijst; val terug op legacy tekst). */
  leverancier: string | null
  leverancierId: string | null
  /** Pincode - zichtbaar (niet gemaskeerd), default 7710. */
  pincode: string
  status: string
  eigenaarBv: { id: string; name: string; korteNaam: string | null } | null
  voertuig: { id: string; kenteken: string; merk: string; model: string } | null
  bestuurder: string | null
  bvBestuurder: { id: string; name: string; korteNaam: string | null } | null
}

// Pincode: 4-8 cijfers; default 7710. Bewust niet-gemaskeerd (zichtbaar in lijst/detail).
const pincodeSchema = z
  .string()
  .trim()
  .regex(/^\d{4,8}$/, 'Pincode bestaat uit 4 tot 8 cijfers')

export const CreateFuelCardSchema = z.object({
  passnummer: z.string().trim().min(1, 'Passnummer is verplicht').max(50),
  leverancierId: idParam('leverancierId'),
  pincode: pincodeSchema.default('7710'),
  // Optioneel: leeg laten => de API default naar STREEP Personeel BV.
  eigenaarBvId: idParam('eigenaarBvId').optional(),
  voertuigId: idParam('voertuigId').nullable().optional(),
  status: z.enum(TANKPAS_STATUSSEN).default('ACTIEF'),
})
export type CreateFuelCardInput = z.infer<typeof CreateFuelCardSchema>

export const UpdateFuelCardSchema = partialZonderDefaults(CreateFuelCardSchema)
export type UpdateFuelCardInput = z.infer<typeof UpdateFuelCardSchema>

// ─── Beheerbare leverancier-lijst ────────────────────────────────────────────

export const CreateTankpasLeverancierSchema = z.object({
  naam: z.string().trim().min(1, 'Naam is verplicht').max(100),
})
export type CreateTankpasLeverancierInput = z.infer<typeof CreateTankpasLeverancierSchema>

export const UpdateTankpasLeverancierSchema = z
  .object({
    naam: z.string().trim().min(1, 'Naam is verplicht').max(100).optional(),
    actief: z.boolean().optional(),
  })
  .refine((v) => v.naam !== undefined || v.actief !== undefined, {
    message: 'Geef minstens één veld om bij te werken',
  })
export type UpdateTankpasLeverancierInput = z.infer<typeof UpdateTankpasLeverancierSchema>
